import React, { useState, useEffect, useCallback } from 'react';
import { Radio, RefreshCw, CheckCircle, AlertTriangle, XCircle, HelpCircle, Clock, ExternalLink, Info, ChevronDown } from 'lucide-react';

const WS_URL = process.env.REACT_APP_WS_URL || 'wss://51h1n29vk7.execute-api.us-west-2.amazonaws.com/production';

const STATUS_CONFIG = {
  OPERATIONAL: { color: 'text-emerald-400', bg: 'bg-emerald-400/10', border: 'border-emerald-400/30', icon: CheckCircle, label: 'Operational' },
  DEGRADED: { color: 'text-amber-400', bg: 'bg-amber-400/10', border: 'border-amber-400/30', icon: AlertTriangle, label: 'Degraded' },
  PARTIAL_OUTAGE: { color: 'text-orange-400', bg: 'bg-orange-400/10', border: 'border-orange-400/30', icon: AlertTriangle, label: 'Partial Outage' },
  MAJOR_OUTAGE: { color: 'text-red-400', bg: 'bg-red-400/10', border: 'border-red-400/30', icon: XCircle, label: 'Major Outage' },
  UNKNOWN: { color: 'text-text-muted', bg: 'bg-surface-2', border: 'border-border', icon: HelpCircle, label: 'Unknown' },
};

const METHOD_LABELS = { FEED: 'Feed', AI_SCRAPER: 'AI Scraper', SYNTHETIC_CANARY: 'Canary', SNOW_CORRELATION: 'INC Override', CACHED: 'Cached' };

const VENDOR_URLS = {
  'AWS': 'https://health.aws.amazon.com/health/status',
  'Cloudflare': 'https://www.cloudflarestatus.com/',
  'OracleOCI': 'https://ocistatus.oraclecloud.com/',
  'Oracle OCI': 'https://ocistatus.oraclecloud.com/',
  'Dynatrace': 'https://status.dynatrace.com/',
  'LexisNexis': 'https://status.lexisnexisrisk.com/',
  'Zscaler': 'https://trust.zscaler.com/',
  'Google Workspace': 'https://www.google.com/appsstatus/dashboard/',
  'Google Cloud': 'https://status.cloud.google.com/',
  'SolarWinds Orion': 'https://status.cloud.solarwinds.com/',
  'xMatters': 'https://status.xmatters.com/',
  'LumApps': 'https://status.lumapps.com/',
  'Apigee': 'https://status.apigee.com/',
  'Shopify': 'https://www.shopifystatus.com/',
  'SmartyStreets': 'https://status.smarty.com/',
  'LUMEN': 'https://lumen.statuspage.io/',
  'Equifax': 'https://status.equifaxworkforce.com/',
  'Wavelo': 'https://www.wavelo.com/',
  'ServiceNow': 'https://status.service-now.com/',
  'F5': 'https://www.f5cloudstatus.com/',
  'Zinc': 'https://status.zinc.it/',
  'Salesforce': 'https://status.salesforce.com/',
  'Ping Identity': 'https://status.pingidentity.com/',
  'OKTA': 'https://status.okta.com/',
  'AT&T': 'https://status-officeathand.att.com/',
  'CenturyLink': 'https://lumen.statuspage.io/',
  'UKG': 'https://trustcenter.ukg.com/',
  'CyberSource': 'https://status.cybersource.com/',
  'Neustar': 'https://statusgator.com/services/neustar',
  'Adobe Commerce': 'https://status.adobe.com/',
  'Beyond Trust': 'https://downdetector.com/status/beyondtrust/',
  'Proofpoint': 'https://downdetector.com/status/proofpoint/',
  // Tier 1 — external SaaS
  'Snowflake': 'https://status.snowflake.com/',
  'Databricks': 'https://status.databricks.com/',
  'Tableau': 'https://status.salesforce.com/',
  'Qualtrics': 'https://status.qualtrics.com/',
  'Gemini': 'https://status.cloud.google.com/',
  'Ketch': 'https://status.ketch.com/',
  'Cortex XSIAM': 'https://status.paloaltonetworks.com/',
  'Vertex': 'https://status.vertexinc.com/',
  'Oracle OFSC': 'https://ocistatus.oraclecloud.com/',
  'Harness': 'https://status.harness.io/',
  'Concur': 'https://www.concursolutions.com/',
  'Mimecast': 'https://status.mimecast.com/',
  'Logz.io': 'https://status.logz.io/',
  'Recurly': 'https://status.recurly.com/',
  'Freshdesk': 'https://status.freshworks.com/',
  'ASAPP': 'https://status.asapp.com/',
  'DOMO': 'https://status.domo.com/',
  'Confluent': 'https://status.confluent.cloud/',
  'OneStream': 'https://onestream.statuspage.io/',
  'SecureAuth': 'https://status.secureauth.com/',
  // Tier 2 — DISH-owned public sites
  'Sling.com': 'https://www.sling.com/',
  'DISH.com': 'https://www.dish.com/',
  'Boost Mobile': 'https://www.boostmobile.com/',
  'Boost Infinite': 'https://www.boostinfinite.com/',
  'Genesis 5G': 'https://www.genesis5g.com/',
  'HughesNet': 'https://www.hughesnet.com/',
  'OnTech Smart Services': 'https://www.ontechsmartservices.com/',
  'Boost Mobile Gear': 'https://www.boostmobilegear.com/',
  'AirTV': 'https://www.airtv.net/',
  'Boost ID': 'https://id.boostmobile.com/',
  'DISH Careers': 'https://careers.dish.com/',
};

const VENDOR_DESCRIPTIONS = {
  'Apigee': 'API gateway platform managing EchoStar customer-facing and internal service APIs',
  'AT&T': 'Network backbone provider for DISH Wireless and satellite connectivity',
  'AWS': 'Enterprise cloud infrastructure powering IT services across all EchoStar business orgs',
  'CenturyLink': 'Legacy circuit and MPLS provider for facility interconnects (now LUMEN)',
  'Cloudflare': 'CDN, DDoS protection, and DNS for customer-facing web properties',
  'Dynatrace': 'Full-stack observability platform monitoring all production applications',
  'Equifax': 'Credit verification and identity services for customer onboarding (Finance & Compliance)',
  'F5': 'Load balancing and application delivery for on-premise data centers',
  'Google Cloud': 'Secondary cloud provider for analytics, BigQuery, and ML workloads',
  'Google Workspace': 'Enterprise email, docs, and collaboration suite for all employees',
  'LexisNexis': 'Risk & compliance — identity verification, fraud screening via Bridger/ThreatMetrix',
  'LUMEN': 'Enterprise fiber, SD-WAN, and edge compute for distributed facilities',
  'LumApps': 'Corporate intranet and employee communications platform',
  'OKTA': 'Cloud SSO and identity management for enterprise applications (IT-owned)',
  'Oracle OCI': 'Cloud infrastructure for Oracle-based ERP and database workloads',
  'OracleOCI': 'Cloud infrastructure for Oracle-based ERP and database workloads',
  'Ping Identity': 'Customer identity (CIAM) platform for subscriber authentication',
  'Salesforce': 'CRM platform for sales, service, and customer lifecycle management',
  'ServiceNow': 'ITSM platform for change management, incidents, and CMDB (dish.service-now.com)',
  'Shopify': 'E-commerce platform for DISH retail and merchandise storefronts',
  'SmartyStreets': 'Address validation and geocoding for customer provisioning workflows',
  'SolarWinds Orion': 'Network monitoring for on-premise switches, routers, and infrastructure',
  'UKG': 'Human capital management — payroll, scheduling, and workforce analytics',
  'Wavelo': 'Cloud BSS/OSS — provisioning, billing, and subscription management for Retail Wireless',
  'xMatters': 'Incident alerting and on-call rotation for IT operations teams',
  'Zinc': 'Chat application for IHS Field Teams',
  'Zscaler': 'Zero-trust network access and secure web gateway for all employees',
  'CyberSource': 'Visa payment gateway for ecommerce, national channel, and Retail Wireless transactions',
  'Neustar': 'Number porting and NPAC services for wireless number management',
  'Adobe Commerce': 'Magento-based e-commerce platform powering Sling storefronts',
  'Beyond Trust': 'Privileged access management and remote support for secure infrastructure',
  'Proofpoint': 'Email security gateway protecting against phishing, malware, and spam',
  // Tier 1 — external SaaS
  'Snowflake': 'Cloud data warehouse for analytics and business intelligence',
  'Databricks': 'Unified data engineering and ML analytics platform',
  'Tableau': 'Business intelligence dashboards and data visualization',
  'Qualtrics': 'Experience management — customer and employee surveys',
  'Gemini': 'Google generative AI for Workspace and enterprise apps',
  'Ketch': 'Privacy consent and CCPA/GDPR compliance management',
  'Cortex XSIAM': 'Palo Alto AI-driven security operations (SIEM/SOAR)',
  'Vertex': 'Tax calculation engine for commerce transactions',
  'Oracle OFSC': 'Oracle Field Service Cloud — technician dispatch and scheduling',
  'Harness': 'Cloud cost management and CI/CD delivery platform',
  'Concur': 'SAP travel and expense management for employees',
  'Mimecast': 'Email security and archiving (secondary to Proofpoint)',
  'Logz.io': 'Cloud observability — logs, metrics, and tracing',
  'Recurly': 'Subscription billing and revenue management for Sling',
  'Freshdesk': 'Customer support ticketing for Sling subscribers',
  'ASAPP': 'AI-powered customer service chat and voice automation',
  'DOMO': 'Business intelligence dashboards and data apps',
  'Confluent': 'Managed Kafka event streaming platform',
  'OneStream': 'Financial consolidation and planning platform',
  'SecureAuth': 'MFA and SSO identity provider',
  // Tier 2 — DISH-owned public sites
  'Sling.com': 'Sling TV consumer streaming website',
  'DISH.com': 'DISH TV consumer and marketing website',
  'Boost Mobile': 'Boost Mobile consumer wireless website',
  'Boost Infinite': 'Boost Infinite postpaid wireless website',
  'Genesis 5G': 'Genesis 5G wireless brand website',
  'HughesNet': 'HughesNet satellite internet website',
  'OnTech Smart Services': 'OnTech Smart Services in-home tech support site',
  'Boost Mobile Gear': 'Boost Mobile device and accessories store',
  'AirTV': 'AirTV streaming device product website',
  'Boost ID': 'Boost Mobile customer identity and login gateway',
  'DISH Careers': 'DISH corporate careers and recruiting portal',
};

// LOB mapping — vendors with multiple LOBs appear under each
const VENDOR_LOB = {
  'Apigee': ['Shared Services'],
  'AT&T': ['Wireless', 'DBS', 'Hughes'],
  'AWS': ['Shared Services', 'Wireless', 'DBS', 'Sling', 'Hughes'],
  'CenturyLink': ['Shared Services'],
  'Cloudflare': ['Shared Services', 'Sling'],
  'CyberSource': ['Wireless', 'DBS'],
  'Dynatrace': ['Shared Services'],
  'Equifax': ['Wireless', 'DBS'],
  'F5': ['Shared Services'],
  'Google Cloud': ['Shared Services', 'Sling'],
  'Google Workspace': ['Shared Services'],
  'LexisNexis': ['Wireless', 'DBS'],
  'LUMEN': ['Shared Services', 'DBS'],
  'LumApps': ['Shared Services'],
  'Neustar': ['Wireless'],
  'OKTA': ['Shared Services'],
  'Oracle OCI': ['Shared Services', 'DBS'],
  'OracleOCI': ['Shared Services', 'DBS'],
  'Ping Identity': ['Wireless', 'DBS'],
  'Salesforce': ['Wireless', 'DBS', 'Media Sales'],
  'ServiceNow': ['Shared Services'],
  'Shopify': ['Wireless', 'DBS'],
  'SmartyStreets': ['Wireless', 'DBS'],
  'SolarWinds Orion': ['Shared Services'],
  'UKG': ['Shared Services'],
  'Wavelo': ['Wireless'],
  'xMatters': ['Shared Services'],
  'Zinc': ['On Tech'],
  'Zscaler': ['Shared Services'],
  'Adobe Commerce': ['Sling'],
  'Beyond Trust': ['Shared Services'],
  'Proofpoint': ['Shared Services'],
  // Tier 1 — external SaaS
  'Snowflake': ['DBS'],
  'Databricks': ['Shared Services'],
  'Tableau': ['Shared Services'],
  'Qualtrics': ['DBS'],
  'Gemini': ['Shared Services'],
  'Ketch': ['Shared Services'],
  'Cortex XSIAM': ['Shared Services'],
  'Vertex': ['Shared Services'],
  'Oracle OFSC': ['On Tech'],
  'Harness': ['Shared Services'],
  'Concur': ['Shared Services'],
  'Mimecast': ['Shared Services'],
  'Logz.io': ['Shared Services'],
  'Recurly': ['Sling'],
  'Freshdesk': ['Sling'],
  'ASAPP': ['Wireless', 'DBS'],
  'DOMO': ['DBS'],
  'Confluent': ['Shared Services', 'Wireless'],
  'OneStream': ['Shared Services'],
  'SecureAuth': ['Shared Services'],
  // Tier 2 — DISH-owned public sites
  'Sling.com': ['Sling'],
  'DISH.com': ['DBS'],
  'Boost Mobile': ['Wireless'],
  'Boost Infinite': ['Wireless'],
  'Genesis 5G': ['Wireless'],
  'HughesNet': ['Hughes'],
  'OnTech Smart Services': ['On Tech'],
  'Boost Mobile Gear': ['Wireless'],
  'AirTV': ['DBS'],
  'Boost ID': ['Wireless'],
  'DISH Careers': ['Shared Services'],
};

const LOB_OPTIONS = ['All', 'Shared Services', 'Wireless', 'DBS', 'Sling', 'On Tech', 'Media Sales', 'Hughes'];

const Skeleton = ({ className }) => <div className={`bg-surface-2 rounded animate-pulse ${className}`} />;

const VendorStatusSkeleton = () => (
  <div className="p-6 max-w-5xl mx-auto">
    <div className="flex items-center justify-center py-12 mb-6">
      <div className="relative mr-3">
        <div className="w-8 h-8 border-2 border-border rounded-full" />
        <div className="absolute inset-0 w-8 h-8 border-2 border-t-blue-400 rounded-full animate-spin" />
      </div>
      <p className="text-sm text-text-secondary">Loading vendor statuses...</p>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="bg-surface border border-border rounded-card p-5">
          <Skeleton className="h-4 w-28 mb-3" />
          <Skeleton className="h-6 w-20 mb-2" />
          <Skeleton className="h-3 w-full" />
        </div>
      ))}
    </div>
  </div>
);

const VendorCard = ({ vendor }) => {
  const cfg = STATUS_CONFIG[vendor.globalStatus] || STATUS_CONFIG.UNKNOWN;
  const Icon = cfg.icon;
  const checked = vendor.lastChecked ? new Date(vendor.lastChecked).toLocaleTimeString() : '—';
  const url = VENDOR_URLS[vendor.vendorName];
  const description = VENDOR_DESCRIPTIONS[vendor.vendorName];

  const cardContent = (
    <>
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-semibold text-text-primary">{vendor.vendorName}</h3>
        <div className="flex items-center gap-2">
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-surface-2 text-text-muted border border-border">
            {METHOD_LABELS[vendor.ingestionMethod] || vendor.ingestionMethod}
          </span>
          {url && <ExternalLink size={12} className="text-text-muted opacity-0 group-hover:opacity-100 transition-opacity" />}
        </div>
      </div>
      {description && <p className="text-[11px] text-text-muted mb-3">{description}</p>}
      <div className={`flex items-center gap-2 mb-2 ${cfg.color}`}>
        <Icon size={18} />
        <span className="text-sm font-medium">{cfg.label}</span>
      </div>
      {vendor.rawIncidentMessage && vendor.globalStatus !== 'OPERATIONAL' && (
        <p className="text-xs text-text-secondary mt-2 line-clamp-2">{vendor.rawIncidentMessage}</p>
      )}
      <div className="flex items-center gap-1 mt-3 text-[11px] text-text-muted">
        <Clock size={11} />
        <span>Checked {checked}</span>
      </div>
    </>
  );

  if (!url) {
    return (
      <div className={`bg-surface border ${cfg.border} rounded-card p-5`}>
        {cardContent}
      </div>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`block bg-surface border ${cfg.border} rounded-card p-5 transition-all hover:shadow-md hover:scale-[1.01] cursor-pointer group`}
    >
      {cardContent}
    </a>
  );
};

const VendorStatus = () => {
  const [vendors, setVendors] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showMethodology, setShowMethodology] = useState(false);
  const [selectedLob, setSelectedLob] = useState('All');

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);
    try {
      const ws = new WebSocket(WS_URL);
      ws.onopen = () => ws.send(JSON.stringify({ action: 'vendor_status' }));
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.vendors) {
            setVendors(msg.vendors);
            setLastUpdated(msg.lastUpdated || new Date().toISOString());
          }
        } catch (e) { console.error('VendorStatus parse error:', e); }
        ws.close();
        setLoading(false);
      };
      ws.onerror = () => { setError('Connection failed'); setLoading(false); };
      ws.onclose = () => setLoading(false);
      setTimeout(() => { try { ws.close(); } catch (e) {} setLoading(false); }, 30000);
    } catch (e) { setError(e.message); setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { const i = setInterval(fetchData, 300000); return () => clearInterval(i); }, [fetchData]);

  if (loading && !vendors) return <VendorStatusSkeleton />;

  const operational = vendors ? vendors.filter(v => v.globalStatus === 'OPERATIONAL').length : 0;
  const total = vendors ? vendors.length : 0;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Radio size={20} className="text-text-secondary" />
          <div>
            <h1 className="text-lg font-semibold text-text-primary">Vendor Status</h1>
            <p className="text-xs text-text-muted">
              {operational}/{total} operational
              {lastUpdated && <> · Last updated {new Date(lastUpdated).toLocaleTimeString()}</>}
            </p>
          </div>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-1.5 text-xs rounded-lg bg-surface border border-border text-text-secondary hover:text-text-primary hover:bg-surface-2 transition-colors disabled:opacity-50"
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* LOB Filter */}
      <div className="mb-4 flex items-center gap-3">
        <label htmlFor="lob-filter" className="text-xs text-text-secondary font-medium">Filter by Line of Business:</label>
        <select
          id="lob-filter"
          value={selectedLob}
          onChange={(e) => setSelectedLob(e.target.value)}
          className="text-xs px-3 py-1.5 rounded-lg bg-surface border border-border text-text-primary focus:outline-none focus:ring-1 focus:ring-blue-400/50 cursor-pointer"
        >
          {LOB_OPTIONS.map(lob => (
            <option key={lob} value={lob}>{lob}</option>
          ))}
        </select>
        {selectedLob !== 'All' && (
          <span className="text-[11px] text-text-muted">
            Showing {vendors ? vendors.filter(v => (VENDOR_LOB[v.vendorName] || []).includes(selectedLob)).length : 0} vendors
          </span>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 rounded-card bg-red-500/10 border border-red-500/20 text-sm text-red-400">{error}</div>
      )}

      {/* Methodology - expandable */}
      <div className="mb-5">
        <button
          onClick={() => setShowMethodology(!showMethodology)}
          className="flex items-center gap-2 text-xs text-text-secondary hover:text-text-primary transition-colors"
        >
          <ChevronDown size={14} className={`transition-transform ${showMethodology ? 'rotate-180' : ''}`} />
          <span>How we determine if a vendor service is OPERATIONAL</span>
        </button>
        {showMethodology && (
          <div className="mt-3 p-4 bg-surface border border-border rounded-card text-[12px] text-text-secondary leading-5 space-y-3">
            <p>Every vendor is checked with <span className="text-text-primary font-medium">two independent signals</span> running in parallel:</p>
            <ol className="list-decimal pl-4 space-y-1">
              <li><span className="text-text-primary">Primary signal</span> (Feed or AI Scraper) — "What does the vendor say?"</li>
              <li><span className="text-text-primary">Canary probe</span> — "Can we actually reach their service?"</li>
            </ol>
            <p className="text-text-primary font-medium pt-1">Decision logic:</p>
            <ul className="space-y-1.5 pl-1">
              <li><span className="text-emerald-400">●</span> Primary = OPERATIONAL + Canary = OPERATIONAL → <span className="text-emerald-400 font-medium">OPERATIONAL</span></li>
              <li><span className="text-amber-400">●</span> Primary reports DEGRADED/OUTAGE → <span className="text-amber-400 font-medium">DEGRADED/OUTAGE</span> (trust the vendor)</li>
              <li><span className="text-emerald-400">●</span> Primary fails but Canary confirms gateway alive → <span className="text-emerald-400 font-medium">OPERATIONAL</span> (service reachable, can't read their status page)</li>
              <li><span className="text-red-400">●</span> Canary fails (5xx, timeout, DNS failure) → <span className="text-red-400 font-medium">MAJOR_OUTAGE</span> (can't reach them at all)</li>
              <li><span className="text-amber-400">●</span> Canary latency spikes &gt;3× rolling baseline → <span className="text-amber-400 font-medium">DEGRADED</span> (service is slow)</li>
              <li><span className="text-red-400">●</span> Active ServiceNow INC exists against vendor → <span className="text-red-400 font-medium">Override to DEGRADED/OUTAGE</span> (our team already knows)</li>
            </ul>
            <p className="pt-2 text-text-muted italic">We don't just trust what the vendor says, and we don't just ping their front door. We do both, cross-reference them, check our own incident records, and apply the worst-case signal as the final status.</p>
          </div>
        )}
      </div>

      {/* Vendor Cards Grid + Legend */}
      {vendors && (
        <div className="flex gap-6">
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...vendors]
              .filter(v => selectedLob === 'All' || (VENDOR_LOB[v.vendorName] || []).includes(selectedLob))
              .sort((a, b) => a.vendorName.localeCompare(b.vendorName))
              .map((v) => <VendorCard key={v.vendorName} vendor={v} />)}
          </div>
          <div className="hidden lg:block w-64 shrink-0">
            <div className="bg-surface border border-border rounded-card p-4 sticky top-4">
              <div className="flex items-center gap-2 mb-3">
                <Info size={13} className="text-text-muted" />
                <span className="text-xs font-medium text-text-secondary">How We Monitor</span>
              </div>
              <div className="space-y-3 text-[11px] text-text-muted">
                <div>
                  <span className="font-medium text-text-secondary">Feed</span>
                  <p className="mt-0.5">Vendor's official status API (RSS/JSON). Reflects what the vendor publicly reports. Checked every 3 min.</p>
                </div>
                <div>
                  <span className="font-medium text-text-secondary">AI Scraper</span>
                  <p className="mt-0.5">AI reads vendor status/portal pages via Bedrock to detect outage announcements. Checked every 30 min.</p>
                </div>
                <div>
                  <span className="font-medium text-text-secondary">Canary</span>
                  <p className="mt-0.5">Direct probe to vendor API gateway. Confirms network path, DNS, and gateway responsiveness. Checked every 3 min. Flags DEGRADED if latency spikes &gt;3× baseline.</p>
                </div>
                <div>
                  <span className="font-medium text-text-secondary">SNOW Correlation</span>
                  <p className="mt-0.5">Overrides status if an active Incident (INC) is filed against this vendor's CI in ServiceNow within the last 60 min.</p>
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-border text-[10px] text-text-muted">
                <p>Refreshed every 3 min (feeds/canary) and 30 min (AI scraper). Dashboard serves from cache for instant load.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorStatus;
